"use client";

import { useState, useEffect } from "react";
import {
  UserPlus,
  ChevronDown,
  ChevronUp,
  Phone,
  CheckCircle2,
  MessageSquare,
  Send,
  Loader2,
  Pencil,
  Trash2,
} from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { Badge } from "@/app/_module/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/app/_module/components/ui/alert-dialog";
import { formatDistanceToNow } from "date-fns";

import { formatPhoneNumber } from "@/app/_module/utils/phoneUtils";
import { a2pService } from "@/app/_module/services/a2pService";
import { useErsSessions, useMarkErsConverted, useSendErsMessages, useDeleteErsSession } from "../hooks/ers-queries";
import { ErsContactStatusBadge } from "./ErsContactStatusBadge";
import { ErsCollectModal } from "./ErsCollectModal";
import type { ErsSession } from "../types/ers.types";

interface ErsSectionProps {
  leadId: string;
  leadData?: any;
  policies?: any[];
}

function SessionCard({
  session,
  leadId,
  canSend,
  onEdit,
}: {
  session: ErsSession;
  leadId: string;
  canSend: boolean;
  onEdit: (session: ErsSession) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const markConverted = useMarkErsConverted();
  const sendMessages = useSendErsMessages();
  const deleteSession = useDeleteErsSession();

  const hasPending = session.contacts?.some((c) => c.smsStatus === "pending");

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Session header */}
      <div
        role="button"
        tabIndex={0}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors cursor-pointer"
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => e.key === "Enter" || e.key === " " ? setExpanded(!expanded) : undefined}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-sm">
            <UserPlus className="h-4 w-4 text-gray-400" />
            <span className="font-medium">{session.contactsCount} contacts</span>
          </div>
          <Badge variant="outline" className="text-xs">
            {session.status}
          </Badge>
          <span className="text-xs text-gray-500">
            {formatDistanceToNow(new Date(session.createdAt), { addSuffix: true })}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {session.sentCount > 0 && (
            <div className="flex items-center gap-1 text-xs text-blue-600">
              <Send className="h-3 w-3" />
              {session.sentCount} sent
            </div>
          )}
          {session.repliedCount > 0 && (
            <div className="flex items-center gap-1 text-xs text-green-600">
              <MessageSquare className="h-3 w-3" />
              {session.repliedCount} replied
            </div>
          )}
          {session.convertedCount > 0 && (
            <div className="flex items-center gap-1 text-xs text-primary">
              <CheckCircle2 className="h-3 w-3" />
              {session.convertedCount} converted
            </div>
          )}
          {session.status === "draft" && (
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 transition-colors px-1"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(session);
              }}
              title="Edit draft"
            >
              <Pencil className="h-3 w-3" />
              Edit
            </button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors px-1"
                onClick={(e) => e.stopPropagation()}
                title="Delete session"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent onClick={(e) => e.stopPropagation()}>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this session?</AlertDialogTitle>
                <AlertDialogDescription>
                  {session.status === "draft"
                    ? "This will permanently delete the session, all contacts, and the auto-created lead records. This cannot be undone."
                    : "This will remove the session and contact tracking records. Any leads created from this session will remain. This cannot be undone."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => deleteSession.mutate({ sessionId: session.id, leadId })}
                >
                  {deleteSession.isPending ? "Deleting..." : "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded contacts list */}
      {expanded && (
        <div className="border-t">
          {/* Send button for draft sessions */}
          {hasPending && session.status === "draft" && (
            <div className="px-3 py-2 bg-yellow-50 border-b flex items-center justify-between">
              <span className="text-xs text-yellow-700">
                {session.contacts?.filter((c) => c.smsStatus === "pending").length} messages not
                yet sent
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => sendMessages.mutate({ sessionId: session.id, leadId })}
                disabled={sendMessages.isPending || !canSend}
                title={!canSend ? "A2P verification required to send SMS" : undefined}
              >
                {sendMessages.isPending ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Send className="h-3 w-3 mr-1" />
                )}
                Send Now
              </Button>
            </div>
          )}

          {session.contacts?.map((contact) => (
            <div
              key={contact.id}
              className="px-3 py-2.5 border-b last:border-b-0 flex items-center justify-between hover:bg-gray-50/50"
            >
              <div className="flex items-center gap-3">
                <div>
                  <div className="text-sm font-medium">{contact.name}</div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Phone className="h-3 w-3" />
                    {formatPhoneNumber(contact.phone)}
                    {contact.relationship && (
                      <span className="text-gray-400">
                        &middot; {contact.relationship}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ErsContactStatusBadge type="sms" status={contact.smsStatus} />
                <ErsContactStatusBadge type="reply" status={contact.replyStatus} />
                {contact.replyStatus === "replied" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs text-green-700 border-green-200 hover:bg-green-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      markConverted.mutate({ contactId: contact.id, leadId: session.leadId });
                    }}
                    disabled={markConverted.isPending}
                  >
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Convert
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ErsSection({ leadId, leadData, policies }: ErsSectionProps) {
  const [showModal, setShowModal] = useState(false);
  const [editingSession, setEditingSession] = useState<ErsSession | null>(null);
  const [canSend, setCanSend] = useState(false);
  const { data: sessions, isLoading } = useErsSessions(leadId);

  // Check A2P status once on mount so "Send Now" buttons are gated correctly
  useEffect(() => {
    a2pService.canSendSMS()
      .then((result) => setCanSend(result.canSend))
      .catch(() => setCanSend(false));
  }, []);

  const sessionsList = Array.isArray(sessions) ? sessions : [];
  const latestPolicyId = policies?.[0]?.id;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Referral Hub</h3>
          {sessionsList.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {sessionsList.length} session{sessionsList.length !== 1 ? "s" : ""}
            </Badge>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => setShowModal(true)}
        >
          <UserPlus className="h-4 w-4 mr-1" />
          Collect Referrals
        </Button>
      </div>

      {/* Sessions list */}
      {isLoading && (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading...
        </div>
      )}

      {!isLoading && sessionsList.length === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm">
          <UserPlus className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No referrals collected yet.</p>
          <p className="text-xs mt-1">
            Ask your client for family and friends who might need coverage.
          </p>
        </div>
      )}

      {!isLoading &&
        sessionsList.map((session: ErsSession) => (
          <SessionCard
            key={session.id}
            session={session}
            leadId={leadId}
            canSend={canSend}
            onEdit={setEditingSession}
          />
        ))}

      {/* Collect / Edit Modal */}
      <ErsCollectModal
        open={showModal || !!editingSession}
        onClose={() => {
          setShowModal(false);
          setEditingSession(null);
        }}
        leadId={leadId}
        leadName={leadData?.name || "Client"}
        policyId={latestPolicyId}
        session={editingSession || undefined}
      />
    </div>
  );
}
